<script lang="ts">
import { defineComponent } from 'vue';
import ClipboardIcon from '@/components/clipboard-icon.vue';
import Decimal from 'decimal.js-light';
import Editor from '@/components/event/editor.vue';
import { mapActions } from 'pinia';
import { useFixturesStore } from '@/stores/fixtures';
import { formatMoney } from '@/utils/format';
import BetGuidsOverlay from '@/components/event/market/BetGuidsOverlay.vue';
import { useAppStore } from '@/stores/app';

export default defineComponent({
  name: 'markets',
  components: { BetGuidsOverlay, ClipboardIcon, Editor },
  computed: {
    sortedMarkets() {
      for (let i = 0; i < this.markets.length; i++) {
        this.markets[i].bets = 0;
        for (let j = 0; j < this.markets[i].Selections.length; j++) {
          const selection = this.markets[i].Selections[j];
          if (selection._count.accepted) {
            this.markets[i].bets += selection._count.accepted;
          }
        }
      }

      return this.markets.toSorted((m1: any, m2: any) => {
        if ((m1.bets || m2.bets) && m1.bets > m2.bets) return -1;
        if ((m1.bets || m2.bets) && m1.bets < m2.bets) return 1;
        return +m1.InPlay < +m2.InPlay
          ? -1
          : !!m1.Handicap < !!m2.Handicap
            ? -1
            : m1.MarketTypeId > m1.MarketTypeId
              ? -1
              : m1.Handicap < m2.Handicap;
      });
    },
  },
  data() {
    return {
      finishers: [] as boolean[],
      finishingMarkets: {} as { [key: number]: any },
    };
  },
  methods: {
    formatMoney,
    ...mapActions(useFixturesStore, ['finalizeMarket', 'resetMarket']),
    ...mapActions(useAppStore, ['canWriteSection']),
    sortSelections(selections: any[]) {
      if (!selections || !Array.isArray(selections)) return selections;
      return selections.sort((a, b) => (a.Name > b.Name ? 1 : -1));
    },
    calculateMargin(selections: any[]) {
      if (!selections || !Array.isArray(selections)) return 0;

      let prob = selections.reduce(
        (acc, odd) =>
          parseFloat(odd.Decimal) > 1 && odd.TradingStatus === 'Trading'
            ? acc.plus(new Decimal(1).div(odd.Decimal))
            : acc,
        new Decimal(0),
      );

      return prob.gt(0) ? prob.mul(100).minus(100).toFixed(2) + '%' : '—';
    },
    selectionData({
                    AdditionalResultData: ad,
                    _count,
                  }: {
      Id: number;
      TradingStatus: string;
      AdditionalResultData: { [_: string]: any };
      _count: { accepted: number; rejected: number };
    }) {
      const data = [];
      if (ad) {
        for (const key of Object.keys(ad)) {
          let i = key;
          const renames: { [key: string]: string } = {
            PercentageWin: '<i class="pi pi-crown text-[11px]"></i>&nbsp;',
            PercentagePush: '<i class="pi pi-refresh text-[11px]"></i>&nbsp;',
            CountInPlace: '/&nbsp;',
            Place: '<i class="pi pi-trophy text-[13px]"></i>&nbsp;',
          };
          for (const r of Object.keys(renames)) {
            i = i.replace(r, renames[r]);
          }
          if (ad[key]) data.push(`${i}${ad[key]}`);
        }
      }
      return data.join('&nbsp;');
    },
    selectionBetsCount({ _count }: {
      _count: { accepted: number; rejected: number };
    }) {
      if (_count.accepted || _count.rejected) {
        return `Ставки: ${_count.accepted}/${_count.rejected}`;
      }
    },
    catchSelection(selection: any) {
      if (selection.ResultStatus) {
        if (!this.finishingMarkets[selection.MarketId]) {
          this.finishingMarkets[selection.MarketId] = { [selection.Id]: selection };
        } else {
          this.finishingMarkets[selection.MarketId][selection.Id] = selection;
        }
      } else {
        if (this.finishingMarkets[selection.MarketId]) {
          delete this.finishingMarkets[selection.MarketId][selection.Id];
        }
        if (!Object.keys(this.finishingMarkets[selection.MarketId]).length) {
          delete this.finishingMarkets[selection.MarketId];
        }
      }
    },
    async sendSelections(id: number, i: number) {
      const market = this.markets.find((m: { Id: number }) => m.Id === id);

      if (this.finishingMarkets[id]) {
        const success = await this.finalizeMarket(this.finishingMarkets[id], market.FixtureId);

        if (!success) {
          return;
        }

        for (const selectionId of Object.keys(this.finishingMarkets[id])) {
          const selection = market.Selections.find((s: { Id: number }) => s.Id === +selectionId);

          const index = market.Selections.indexOf(selection);

          market.Selections[index] = this.finishingMarkets[id][selectionId];
        }

        this.finishers[i] = false;
      }
    },
    async resetMarketSelections(marketId: number) {
      this.resetMarket(marketId).then(() => this.$emit('reload'));
    },
  },
  props: ['markets'],
  watch: {
    markets: {
      immediate: true,
      handler(v) {
        this.finishers = new Array(v?.length || 0).fill(false);
      },
    },
  },
});
</script>

<template>
  <div
    v-for="(market, i) in sortedMarkets"
    :class="{
      'mx-3 my-2 p-2 bg-gray-200 dark:bg-gray-800 overflow-visible border-2 rounded border-transparent': true,
      'border-teal-500 dark:border-teal-600': market.PoolEvents.length,
    }"
  >
    <div class="grid grid-cols-[0.42fr_2fr_2fr_0.5fr_1.5fr_1fr] text-sm mb-2">
      <div>
        <span v-if="market.InPlay" class="bg-red-500 text-white p-1 rounded">Live</span>
        <span v-else class="bg-gray-500 text-white p-1 rounded">PreMatch</span>
        <span v-if="market.Handicap" class="bg-gray-500 text-white ml-1 p-1 rounded">HC</span>
      </div>
      <div>
        {{ market.Name }}<br />
        <span class="text-gray-500">{{ market.Id }}</span>
        <clipboard-icon :text="market.Id" icon-class="text-sm ml-1" />
        <clipboard-icon :text="market.Name" icon-class="text-sm ml-1" icon="pi-file-word" />
      </div>
      <div class="text-gray-500">
        {{ market.MarketType.Name }}<br />
        <span class="text-gray-500">{{ market.MarketType.Id }}</span>
        <clipboard-icon :text="market.MarketType.Id" icon-class="text-sm ml-1" />
        <clipboard-icon
          :text="market.MarketType.Name"
          icon-class="text-sm ml-1"
          icon="pi-file-word"
        />
      </div>
      <div class="text-gray-500">
        Маржа<br />
        {{ calculateMargin(market.Selections) }}
      </div>
      <div class="content-center text-center">
        <span v-if="market.Selections.some((s: any) => !s.ResultStatus) && canWriteSection('bets')">
          <template v-if="!finishers[i]">
            <span
              class="text-nowrap px-2 py-1.5 text-sm cursor-pointer rounded bg-yellow-500 text-black"
              @click="finishers[i] = !finishers[i]"
            >
              <i class="pi pi-stopwatch text-xs" />
              Завершить
            </span>
          </template>
          <template v-else>
            <span
              class="text-nowrap px-2 py-1.5 text-sm cursor-pointer rounded bg-red-500"
              @click="finishers[i] = !finishers[i]"
            >
              <i class="pi pi-times text-xs" />
              Отменить
            </span>
            <span
              class="text-nowrap animate-pulse px-2 py-1.5 text-sm cursor-pointer rounded bg-green-500 ml-2"
              v-if="
                finishingMarkets[+market.Id] && Object.keys(finishingMarkets[+market.Id]).length
              "
              @click="sendSelections(market.Id, i)"
            >
              Подтвердить
              <i class="pi pi-arrow-right text-xs" />
            </span>
          </template>
        </span>
        <span v-else-if="canWriteSection('bets')">
          <span
            class="text-nowrap px-2 py-1.5 text-sm text-yellow-500 cursor-pointer"
            @click="resetMarketSelections(market.Id)"
          >
            Сбросить
            <i class="pi pi-times text-xs" />
          </span>
        </span>
      </div>
      <div class="content-center text-right">
        <span
          :class="{
            'ml-auto mt-0.5 font-mono': true,
            'text-blue-500': market.TradingStatus === 'Suspended',
            'text-green-500': market.TradingStatus === 'Open',
            'text-red-500': market.TradingStatus === 'Closed',
          }"
        >
          {{ market.TradingStatus }}
        </span>
      </div>
    </div>
    <div class="flex flex-row flex-wrap gap-1">
      <div
        v-for="selection in sortSelections(market.Selections)"
        :class="{
          'p-2 pb-1 rounded grid grid-cols-[1fr_auto_min-content] grid-place-content-between gap-1': true,
          'bg-gray-300 dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-700': true,
          'border-teal-500 dark:border-teal-600': selection._count.accepted,
        }"
        class="bg-"
        style="min-width: 300px"
      >
        <div>
          <span class="mr-1">{{ selection.Name }}</span>
          <span class="text-gray-400 dark:text-gray-500">{{ selection.Outcome?.Name }}</span>
        </div>

        <div class="text-gray-400 dark:text-gray-500 text-sm mr-1">
          {{ (+selection.Decimal).toFixed(2) }}
        </div>

        <span
          :class="{
            'text-green-500': selection.ResultStatus === 'Winner',
            'text-red-500': selection.ResultStatus === 'Loser',
            'text-yellow-600 dark:text-yellow-500': !['Loser', 'Winner'].includes(
              selection.ResultStatus
            ),
          }"
        >
          <span class="text-right">
            <i
              v-if="selection.ResultStatus === 'Winner'"
              class="pi pi-check font-bold"
              title="Выигрыш"
            />
            <i
              v-else-if="selection.ResultStatus === 'Loser'"
              class="pi pi-times font-bold"
              title="Проигрыш"
            />
            <i
              v-else-if="selection.ResultStatus === 'Partial'"
              class="pi pi-percentage"
              title="Частичный"
            />
            <i v-else-if="selection.ResultStatus === 'Pushed'" class="pi pi-undo" title="Возврат" />
            <span v-else class="text-xs">
              <i
                v-if="!selection.ResultStatus"
                class="pi pi-ellipsis-h font-bold"
                title="Нет результата"
              />
              <span v-else>{{ selection.ResultStatus }}</span>
            </span>
          </span>
        </span>

        <div class="content-center text-xs text-gray-400 dark:text-gray-500 col-span-2">
          <span>{{ selection.Id }}</span>
          <clipboard-icon :text="selection.Id" iconClass="text-xs" class="ml-1" />
          <span class="text-white opacity-30 ml-3" v-html="selectionData(selection)"></span>
          <bet-guids-overlay :counters="selection._count" class="m-1" :text="selectionBetsCount(selection)" />
        </div>

        <div class="text-center">
          <i
            class="pi pi-pause-circle text-gray-500 opacity-40 text-sm"
            v-if="selection.TradingStatus === 'Suspended'"
            title="Приостановлен"
          />
          <i
            class="pi pi-play-circle text-gray-500 opacity-40 text-sm"
            v-if="selection.TradingStatus === 'Trading'"
            title="Торгуется"
          />
          <i
            class="pi pi-dollar text-red-500 text-sm"
            v-if="selection.TradingStatus === 'Unpriced'"
            title="Нет коэффициента"
          />
          <i
            class="pi pi-ban text-red-500 text-sm"
            v-if="selection.TradingStatus === 'NonRunner'"
            title="Не в игре"
          />
        </div>
      </div>
    </div>
    <editor
      v-if="canWriteSection('bets')"
      :market="market"
      @update="catchSelection"
      :class="{
        'mt-2': true,
        'animate-slideup max-h-0': !finishers[i],
        'animate-slidedown max-h-[10000px]': finishers[i],
      }"
    />
  </div>
  <div class="my-28">&nbsp;</div>
</template>

<style scoped lang="scss">
.localOpacity {
  @apply opacity-40;

  &:hover {
    @apply opacity-100;
  }
}
</style>
